import React, { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "./supabase";
import { C, font, mono } from "./config/theme";
import { fmt } from "./utils/format";

/* ═══════════════════════════════════════════════════════════════════════════════
   PAGE ADMIN — Vue multi-tenant sur le schéma bv_* (MCD v14)

   RPCs câblées :
     - bv_rpc_admin_sync_status()                  : stats agrégées par tenant
     - bv_rpc_admin_create_tenant(nom, sub, email) : création tenant + config défaut
     - bv_rpc_admin_delete_tenant(id, confirm)     : suppression 2 étapes (preview puis confirm)

   La liste des tenants reste en query directe sur `tenants` pour inclure les
   inactifs (l'RPC sync_status ne renvoie que actif = true).
   ═══════════════════════════════════════════════════════════════════════════════ */

const TABLES_PRINCIPALES = [
  "bv_chantier", "bv_facture_client", "bv_marche", "bv_chiffrage",
  "bv_pointage_mo", "bv_salarie", "bv_tiers", "bv_achat_fournisseur",
];

/* ─── Helpers ─── */
function fmtDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function fmtDateShort(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function tenantUrl(schema_name) {
  const h = window.location.hostname;
  if (h === "localhost" || h === "127.0.0.1") return null;
  const root = h.split(".").slice(1).join(".");
  return `https://${schema_name}.${root}`;
}

/* Normalisation d'un nom de tenant en sous-domaine (lowercase, alphanum + tirets) */
function nomToSubdomain(nom) {
  return (nom || "")
    .toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "") // accents → ascii
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
}

/* ─── Styles partagés ─── */
const S = {
  card: {
    background: "#fff", borderRadius: 12, padding: "16px 20px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  btnLogout: {
    padding: "6px 16px", fontSize: 12, fontWeight: 600, fontFamily: font,
    background: "rgba(255,255,255,0.1)", color: "#fff",
    border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6, cursor: "pointer",
  },
  btnPrimary: {
    padding: "8px 18px", borderRadius: 8, border: "none", background: C.accent,
    color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: font,
    boxShadow: "0 2px 6px rgba(0,0,0,0.12)", whiteSpace: "nowrap",
  },
  btnSecondary: {
    padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.border}`,
    background: "#fff", color: C.slate, fontSize: 12, fontWeight: 500,
    cursor: "pointer", fontFamily: font,
  },
  btnDanger: {
    padding: "8px 18px", borderRadius: 8, border: "none", background: C.red,
    color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: font,
    boxShadow: "0 2px 6px rgba(220,38,38,0.25)",
  },
  badge: (color) => ({
    padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
    background: `${color}20`, color, whiteSpace: "nowrap",
  }),
  modalOverlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, padding: 20,
  },
  modalBox: {
    background: "#fff", borderRadius: 14, width: "100%", maxWidth: 520,
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)", overflow: "hidden",
  },
  input: {
    width: "100%", padding: "9px 12px", fontSize: 13,
    border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: font,
    color: C.text, outline: "none", boxSizing: "border-box",
  },
  label: { fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 4, display: "block" },
};

/* ═══════════════════════════════════════════════════════════════════════════════
   PAGE PRINCIPALE
   ═══════════════════════════════════════════════════════════════════════════════ */
export default function PageAdmin({ profile, signOut }) {
  /* ─── État données ─── */
  const [tenants, setTenants] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [tableSizes, setTableSizes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);

  /* ─── État UI ─── */
  const [expandedId, setExpandedId] = useState(null);
  const [tenantDetail, setTenantDetail] = useState({});
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [search, setSearch] = useState("");

  /* ─── État modales ─── */
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteState, setDeleteState] = useState(null); // { tenant, summary, loading }

  /* ─── Titre onglet ─── */
  useEffect(() => { document.title = "Admin · BatiViz"; }, []);

  /* ─── Auto-dismiss feedback ─── */
  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => setFeedback(null), 5000);
    return () => clearTimeout(t);
  }, [feedback]);

  /* ─── Chargement initial — RPC sync_status + queries de tableaux ─── */
  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      /* 1. Stats globales (counts uniquement) */
      const [{ count: usersCount }, { count: tenantsActifs }] = await Promise.all([
        supabase.from("profiles").select("*", { count: "exact", head: true }),
        supabase.from("tenants").select("*", { count: "exact", head: true }).eq("actif", true),
      ]);

      /* 2. Liste tenants (inclut inactifs — non couverts par l'RPC) */
      const { data: tenantsData, error: tErr } = await supabase
        .from("tenants")
        .select("id, code, nom, schema_name, actif, sync_last_run, sync_last_status, subscription_plan, max_users, extra_users, onboarding_date")
        .order("nom", { ascending: true });
      if (tErr) throw tErr;

      /* 3. Stats agrégées par tenant — 1 RPC remplace les N+1 queries ! */
      const { data: syncStatusData, error: syncErr } = await supabase.rpc("bv_rpc_admin_sync_status");
      if (syncErr) console.warn("bv_rpc_admin_sync_status indisponible :", syncErr);
      const statsByTenantId = new Map(
        Array.isArray(syncStatusData) ? syncStatusData.map((s) => [s.tenant_id, s]) : []
      );

      /* 4. Merge tenants + stats (fallback sur tenants.sync_last_* si log vide) */
      const enriched = (tenantsData || []).map((t) => {
        const s = statsByTenantId.get(t.id);
        return {
          ...t,
          nb_users: s?.nb_users ?? 0,
          nb_chantiers: s?.nb_chantiers ?? 0,
          nb_factures: s?.nb_factures ?? 0,
          derniere_sync: s?.derniere_sync || t.sync_last_run,
          sync_statut: s?.sync_statut || t.sync_last_status,
        };
      });

      /* 5. Volumétrie tables */
      const sizesResults = await Promise.all(
        TABLES_PRINCIPALES.map(async (tn) => {
          const { count } = await supabase.from(tn).select("*", { count: "exact", head: true });
          return [tn, count || 0];
        })
      );
      const sizes = Object.fromEntries(sizesResults);

      /* 6. Sync logs récents */
      const { data: syncs } = await supabase
        .from("bv_etl_sync_log")
        .select("*")
        .order("started_at", { ascending: false })
        .limit(20);

      setTenants(enriched);
      setGlobalStats({
        nb_tenants: tenantsData?.length || 0,
        nb_tenants_actifs: tenantsActifs || 0,
        nb_users: usersCount || 0,
      });
      setTableSizes(sizes);
      setSyncLogs(syncs || []);
    } catch (err) {
      console.error("Erreur chargement admin:", err);
      setError(err.message || "Erreur de chargement des données admin.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  /* ─── Filtrage tenants ─── */
  const filteredTenants = useMemo(() => {
    if (!search.trim()) return tenants;
    const q = search.toLowerCase();
    return tenants.filter((t) =>
      (t.nom || "").toLowerCase().includes(q) ||
      (t.code || "").toLowerCase().includes(q) ||
      (t.schema_name || "").toLowerCase().includes(q)
    );
  }, [tenants, search]);

  /* ─── Détail tenant (expand) ─── */
  const loadTenantDetail = useCallback(async (tenantId) => {
    if (tenantDetail[tenantId]) return;
    setLoadingDetail(true);
    try {
      const [chantiersRes, facturesCount, salariesRes, tiersRes, facturesData, usersData] = await Promise.all([
        supabase.from("bv_chantier").select("*", { count: "exact", head: true }).eq("tenant_id", tenantId),
        supabase.from("bv_facture_client").select("*", { count: "exact", head: true }).eq("tenant_id", tenantId),
        supabase.from("bv_salarie").select("*", { count: "exact", head: true }).eq("tenant_id", tenantId),
        supabase.from("bv_tiers").select("*", { count: "exact", head: true }).eq("tenant_id", tenantId),
        supabase.from("bv_facture_client").select("montant_ht").eq("tenant_id", tenantId),
        supabase.from("profiles").select("id, email, prenom, nom, role, actif, created_at").eq("tenant_id", tenantId).order("created_at"),
      ]);
      const ca_total = (facturesData.data || []).reduce((a, r) => a + Number(r.montant_ht || 0), 0);

      setTenantDetail((prev) => ({
        ...prev,
        [tenantId]: {
          stats: {
            nb_chantiers: chantiersRes.count || 0,
            nb_factures: facturesCount.count || 0,
            nb_salaries: salariesRes.count || 0,
            nb_tiers: tiersRes.count || 0,
            ca_total,
          },
          users: usersData.data || [],
        },
      }));
    } catch (err) {
      console.error("Erreur chargement détail tenant:", err);
    } finally {
      setLoadingDetail(false);
    }
  }, [tenantDetail]);

  const toggleExpand = useCallback(async (tenantId) => {
    if (expandedId === tenantId) { setExpandedId(null); return; }
    setExpandedId(tenantId);
    await loadTenantDetail(tenantId);
  }, [expandedId, loadTenantDetail]);

  /* ─── Création tenant (callback passé à la modale) ─── */
  const handleCreateTenant = useCallback(async ({ nom, schema_name, email }) => {
    const { data, error: err } = await supabase.rpc("bv_rpc_admin_create_tenant", {
      p_nom: nom,
      p_schema_name: schema_name,
      ...(email ? { p_email_dirigeant: email } : {}),
    });
    if (err) throw err;
    setShowCreateModal(false);
    setFeedback({
      type: "success",
      message: `Tenant « ${data?.nom} » créé (code ${data?.code}, id ${data?.tenant_id?.substring(0, 8)}…).`,
    });
    await loadOverview();
    // Reset le cache détail au cas où
    setTenantDetail({});
    return data;
  }, [loadOverview]);

  /* ─── Suppression tenant : étape 1 = preview ─── */
  const initDelete = useCallback(async (tenant) => {
    setDeleteState({ tenant, summary: null, loading: true });
    try {
      const { data, error: err } = await supabase.rpc("bv_rpc_admin_delete_tenant", {
        p_tenant_id: tenant.id,
        p_confirm: false,
      });
      if (err) throw err;
      setDeleteState({ tenant, summary: data, loading: false });
    } catch (err) {
      setDeleteState(null);
      setFeedback({ type: "error", message: `Échec preview suppression : ${err.message}` });
    }
  }, []);

  /* ─── Suppression tenant : étape 2 = confirm ─── */
  const confirmDelete = useCallback(async () => {
    if (!deleteState?.tenant) return;
    setDeleteState((prev) => ({ ...prev, loading: true }));
    try {
      const { data, error: err } = await supabase.rpc("bv_rpc_admin_delete_tenant", {
        p_tenant_id: deleteState.tenant.id,
        p_confirm: true,
      });
      if (err) throw err;
      setFeedback({
        type: "success",
        message: `Tenant « ${data?.nom} » supprimé (${data?.nb_users_supprimes} user(s), ${data?.nb_chantiers_supprimes} chantier(s), ${data?.nb_factures_supprimees} facture(s)).`,
      });
      setDeleteState(null);
      if (expandedId === deleteState.tenant.id) setExpandedId(null);
      await loadOverview();
    } catch (err) {
      setDeleteState((prev) => prev ? { ...prev, loading: false } : null);
      setFeedback({ type: "error", message: `Échec suppression : ${err.message}` });
    }
  }, [deleteState, expandedId, loadOverview]);

  /* ─── Chargement initial ─── */
  if (loading) {
    return (
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        height: "100vh", background: "#F8FAFC", fontFamily: font,
      }}>
        <div style={{
          width: 36, height: 36, border: `3px solid ${C.navy}`, borderTopColor: "transparent",
          borderRadius: "50%", animation: "spin 1s linear infinite",
        }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#F8FAFC", fontFamily: font }}>
      {/* ─── Header ─── */}
      <header style={{
        background: C.navy, color: "#fff", padding: "14px 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>BatiViz</div>
          <div style={{
            fontSize: 10, fontWeight: 700, background: "rgba(255,255,255,0.15)",
            padding: "3px 10px", borderRadius: 20,
            textTransform: "uppercase", letterSpacing: "1px",
          }}>Admin</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ fontSize: 13, color: "rgba(255,255,255,0.7)" }}>
            {profile?.prenom} {profile?.nom}
          </span>
          <button onClick={signOut} style={S.btnLogout}>Déconnexion</button>
        </div>
      </header>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 32px" }}>
        {/* ─── Feedback global ─── */}
        {feedback && (
          <div style={{
            padding: "12px 16px", marginBottom: 16, borderRadius: 8,
            background: feedback.type === "success" ? "#F0FDF4" : "#FEF2F2",
            color: feedback.type === "success" ? "#16A34A" : "#DC2626",
            border: `1px solid ${feedback.type === "success" ? "#BBF7D0" : "#FECACA"}`,
            display: "flex", justifyContent: "space-between", alignItems: "center",
            fontSize: 13, fontWeight: 600,
          }}>
            <span>{feedback.message}</span>
            <button
              onClick={() => setFeedback(null)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontSize: 16, marginLeft: 12 }}
            >✕</button>
          </div>
        )}

        {error && (
          <div style={{
            padding: "12px 16px", marginBottom: 16, borderRadius: 8,
            background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B",
            fontSize: 13,
          }}>
            <strong>Erreur :</strong> {error}
            <div style={{ marginTop: 6, fontSize: 11 }}>
              Vérifie que le compte admin a bien les policies RLS cross-tenant
              (helper <code>bv_is_admin()</code>) sur les tables bv_*.
            </div>
          </div>
        )}

        {/* ═══ SECTION 1 : Vue d'ensemble ═══ */}
        <SectionTitle>Vue d'ensemble</SectionTitle>

        {globalStats && (
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 16, marginBottom: 24,
          }}>
            <StatCard
              label="Tenants actifs"
              value={`${globalStats.nb_tenants_actifs} / ${globalStats.nb_tenants}`}
              color="#3B82F6"
            />
            <StatCard
              label="Utilisateurs totaux"
              value={globalStats.nb_users}
              color="#8B5CF6"
            />
            <StatCard
              label="Dernière sync globale"
              value={fmtDate(
                tenants
                  .map((t) => t.derniere_sync)
                  .filter(Boolean)
                  .sort((a, b) => new Date(b) - new Date(a))[0]
              )}
              color="#F59E0B"
              small
            />
          </div>
        )}

        {/* Barre d'actions : recherche + nouveau client */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.navy }}>
            Tenants ({filteredTenants.length})
          </h3>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div style={{ position: "relative" }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un tenant…"
                style={{
                  padding: "7px 14px 7px 32px", borderRadius: 8, border: `1px solid ${C.border}`,
                  fontSize: 12, fontFamily: font, width: 240, outline: "none", background: "#fff",
                }}
              />
              <span style={{
                position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                fontSize: 13, color: C.slateLight,
              }}>🔍</span>
            </div>
            <button onClick={() => setShowCreateModal(true)} style={S.btnPrimary}>
              + Nouveau client
            </button>
          </div>
        </div>

        {/* Tableau tenants */}
        <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", marginBottom: 32 }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "1.5fr 0.55fr 0.6fr 0.6fr 1fr 0.5fr 0.4fr",
            gap: 12, padding: "12px 20px", background: "#FAFBFC",
            borderBottom: `1px solid ${C.border}`,
            fontSize: 10, fontWeight: 700, color: C.slate,
            textTransform: "uppercase", letterSpacing: "0.5px",
          }}>
            <div>Tenant</div>
            <div style={{ textAlign: "right" }}>Users</div>
            <div style={{ textAlign: "right" }}>Chantiers</div>
            <div style={{ textAlign: "right" }}>Factures</div>
            <div>Dernière sync</div>
            <div style={{ textAlign: "center" }}>Statut</div>
            <div style={{ textAlign: "center" }}></div>
          </div>

          {filteredTenants.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: C.slate, fontSize: 13 }}>
              {search ? "Aucun tenant ne correspond à la recherche" : "Aucun tenant"}
            </div>
          ) : filteredTenants.map((t, idx) => {
            const isExpanded = expandedId === t.id;
            const isLast = idx === filteredTenants.length - 1;
            const detail = tenantDetail[t.id];
            const syncOk = t.sync_statut === "success";
            const url = tenantUrl(t.schema_name);

            return (
              <React.Fragment key={t.id}>
                <div
                  onClick={() => toggleExpand(t.id)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.5fr 0.55fr 0.6fr 0.6fr 1fr 0.5fr 0.4fr",
                    gap: 12, padding: "14px 20px",
                    borderBottom: (!isExpanded && !isLast) ? `1px solid ${C.border}` : "none",
                    background: isExpanded ? "#FAFBFC" : "transparent",
                    cursor: "pointer", alignItems: "center",
                    opacity: t.actif ? 1 : 0.55, transition: "background 150ms",
                  }}
                >
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{
                        fontSize: 10, color: C.slateLight,
                        transform: isExpanded ? "rotate(90deg)" : "rotate(0)",
                        display: "inline-block", transition: "transform 200ms",
                      }}>▶</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>{t.nom}</span>
                      <span style={{
                        fontSize: 10, color: C.slate, fontFamily: mono,
                        background: C.bg, padding: "1px 6px", borderRadius: 4,
                      }}>
                        {t.schema_name}
                      </span>
                      {t.subscription_plan && (
                        <span style={S.badge(C.blue)}>{t.subscription_plan}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", fontFamily: mono, fontSize: 13, fontWeight: 600, color: C.text }}>
                    {t.nb_users}
                  </div>
                  <div style={{ textAlign: "right", fontFamily: mono, fontSize: 13, fontWeight: 600, color: C.text }}>
                    {t.nb_chantiers}
                  </div>
                  <div style={{ textAlign: "right", fontFamily: mono, fontSize: 13, fontWeight: 600, color: C.text }}>
                    {t.nb_factures}
                  </div>
                  <div style={{ fontSize: 12, color: C.text }}>
                    {fmtDate(t.derniere_sync)}
                    {t.sync_statut && (
                      <div style={{ fontSize: 10, marginTop: 2, color: syncOk ? C.green : C.red }}>
                        {syncOk ? "✓ OK" : "✗ Erreur"}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <span style={{
                      display: "inline-block", width: 10, height: 10, borderRadius: "50%",
                      background: t.actif ? C.green : C.red,
                    }} />
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); initDelete(t); }}
                      title="Supprimer ce tenant"
                      style={{
                        background: "transparent", border: "none", cursor: "pointer",
                        padding: 4, borderRadius: 6, color: C.red,
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#FEE2E2"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>

                {/* Détail tenant */}
                {isExpanded && (
                  <div style={{
                    padding: "16px 20px 20px 44px",
                    background: "#FAFBFC",
                    borderBottom: !isLast ? `1px solid ${C.border}` : "none",
                    animation: "fadeIn 200ms ease-out",
                  }}>
                    <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}`}</style>

                    {loadingDetail && !detail ? (
                      <div style={{ fontSize: 12, color: C.slate }}>Chargement du détail…</div>
                    ) : detail ? (
                      <TenantDetailPanel tenant={t} detail={detail} url={url} />
                    ) : (
                      <div style={{ fontSize: 12, color: C.slate }}>—</div>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* ═══ SECTION 2 : Système ═══ */}
        <SectionTitle>Système</SectionTitle>

        <div style={{
          display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
          gap: 16, marginBottom: 32,
        }}>
          {/* Volumétrie des tables */}
          <div style={S.card}>
            <div style={{
              fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px",
              color: C.navy, marginBottom: 10,
            }}>
              Volumétrie tables principales
            </div>
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <tbody>
                {TABLES_PRINCIPALES.map((t) => (
                  <tr key={t} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: "6px 4px", color: C.text, fontFamily: mono, fontSize: 11 }}>{t}</td>
                    <td style={{
                      padding: "6px 4px", textAlign: "right", fontFamily: mono, fontWeight: 600, color: C.navy,
                    }}>
                      {(tableSizes[t] ?? 0).toLocaleString("fr-FR")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: 10, color: C.slateLight, marginTop: 8 }}>
              Comptage exact via PostgREST (cross-tenant, RLS admin).
            </div>
          </div>

          {/* Sync ETL récents */}
          <div style={S.card}>
            <div style={{
              fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px",
              color: C.navy, marginBottom: 10,
            }}>
              Sync ETL — 20 derniers runs
            </div>
            {syncLogs.length === 0 ? (
              <div style={{ fontSize: 12, color: C.slate, padding: "8px 0" }}>
                Aucun run ETL enregistré pour le moment.
              </div>
            ) : (
              <div style={{ maxHeight: 260, overflowY: "auto" }}>
                <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                  <tbody>
                    {syncLogs.map((log) => {
                      const tenant = tenants.find((t) => t.id === log.tenant_id);
                      const okColor = log.statut === "success" ? C.green : log.statut === "error" ? C.red : log.statut === "partial" ? C.accent : C.slate;
                      return (
                        <tr key={log.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: "6px 4px", color: C.text, fontWeight: 600 }}>
                            {tenant?.nom || log.tenant_id?.substring(0, 8)}
                          </td>
                          <td style={{ padding: "6px 4px", color: C.slate, fontFamily: mono, fontSize: 10 }}>
                            {fmtDateShort(log.started_at)}
                          </td>
                          <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: mono, fontSize: 10 }}>
                            {log.total_inserted ? `${(log.total_inserted + (log.total_updated || 0)).toLocaleString("fr-FR")} lignes` : "—"}
                          </td>
                          <td style={{ padding: "6px 4px", textAlign: "right" }}>
                            <span style={{ ...S.badge(okColor), fontSize: 10 }}>
                              {log.statut}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══ MODALES ═══ */}
      {showCreateModal && (
        <CreateTenantModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateTenant}
        />
      )}

      {deleteState && (
        <DeleteTenantModal
          state={deleteState}
          onCancel={() => setDeleteState(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   COMPOSANTS INTERNES
   ═══════════════════════════════════════════════════════════════════════════════ */

function SectionTitle({ children }) {
  return (
    <h2 style={{
      fontSize: 16, fontWeight: 800, color: C.navy,
      margin: "0 0 16px 0", paddingBottom: 6,
      borderBottom: `2px solid ${C.border}`,
    }}>
      {children}
    </h2>
  );
}

function StatCard({ label, value, color, small = false }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 12, padding: "16px 20px",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ width: 4, height: 20, borderRadius: 2, background: color }} />
        <span style={{
          fontSize: 11, fontWeight: 600, color: C.slate,
          textTransform: "uppercase", letterSpacing: "0.5px",
        }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: small ? 14 : 26, fontWeight: 800, color: C.navy }}>
        {value}
      </div>
    </div>
  );
}

function TenantDetailPanel({ tenant, detail, url }) {
  const { stats, users } = detail;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
        gap: 12,
      }}>
        <DetailStat label="Chantiers" value={stats.nb_chantiers} />
        <DetailStat label="Factures" value={stats.nb_factures} />
        <DetailStat label="Salariés" value={stats.nb_salaries} />
        <DetailStat label="Tiers" value={stats.nb_tiers} />
        <DetailStat label="CA total" value={fmt(stats.ca_total)} mono />
      </div>

      {url && (
        <div style={{ fontSize: 12, color: C.slate }}>
          Sous-domaine :{" "}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: C.blue, fontFamily: mono, textDecoration: "none" }}
          >
            {url} ↗
          </a>
        </div>
      )}

      <div>
        <div style={{
          fontSize: 11, fontWeight: 700, color: C.slate,
          textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6,
        }}>
          Utilisateurs du tenant ({users.length})
        </div>
        {users.length === 0 ? (
          <div style={{ fontSize: 12, color: C.slate, padding: "6px 0" }}>Aucun utilisateur</div>
        ) : (
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                <th style={thStyle}>Nom</th>
                <th style={thStyle}>Email</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Rôle</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Statut</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Créé le</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: "7px 6px", color: C.navy, fontWeight: 600 }}>
                    {[u.prenom, u.nom].filter(Boolean).join(" ") || "—"}
                  </td>
                  <td style={{ padding: "7px 6px", color: C.slate, fontSize: 11 }}>
                    {u.email}
                  </td>
                  <td style={{ padding: "7px 6px", textAlign: "center" }}>
                    <span style={S.badge(
                      u.role === "admin" ? "#1D4ED8"
                      : u.role === "dirigeant" ? "#92400E"
                      : u.role === "conducteur" ? "#15803D"
                      : C.slate
                    )}>
                      {u.role || "user"}
                    </span>
                  </td>
                  <td style={{ padding: "7px 6px", textAlign: "center" }}>
                    <span style={{
                      display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                      background: u.actif ? C.green : C.red,
                    }} />
                  </td>
                  <td style={{ padding: "7px 6px", textAlign: "right", color: C.slate, fontFamily: mono, fontSize: 10 }}>
                    {fmtDateShort(u.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function DetailStat({ label, value, mono: useMono = false }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 8, padding: "10px 12px",
      border: `1px solid ${C.border}`,
    }}>
      <div style={{
        fontSize: 10, color: C.slate, fontWeight: 600,
        textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 16, fontWeight: 700, color: C.navy,
        fontFamily: useMono ? mono : "inherit",
      }}>
        {value}
      </div>
    </div>
  );
}

const thStyle = {
  padding: "6px 6px", textAlign: "left", fontSize: 10, fontWeight: 700,
  color: C.slate, textTransform: "uppercase", letterSpacing: "0.5px",
};

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   MODALE — Création tenant
   ═══════════════════════════════════════════════════════════════════════════════ */
function CreateTenantModal({ onClose, onCreate }) {
  const [nom, setNom] = useState("");
  const [schema, setSchema] = useState("");
  const [schemaTouched, setSchemaTouched] = useState(false);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  /* Auto-générer le sous-domaine tant que l'utilisateur ne l'a pas touché */
  useEffect(() => {
    if (!schemaTouched) setSchema(nomToSubdomain(nom));
  }, [nom, schemaTouched]);

  const handleSubmit = async () => {
    setFormError(null);
    if (!nom.trim()) return setFormError("Nom requis.");
    if (!schema.trim()) return setFormError("Sous-domaine requis.");
    if (!/^[a-z][a-z0-9-]*$/.test(schema)) {
      return setFormError("Sous-domaine : minuscules, chiffres et tirets, commence par une lettre.");
    }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      return setFormError("Email invalide.");
    }
    setSaving(true);
    try {
      await onCreate({ nom: nom.trim(), schema_name: schema.trim(), email: email.trim() });
    } catch (err) {
      setFormError(err.message || "Erreur lors de la création.");
      setSaving(false);
    }
  };

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.border}` }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.navy }}>Nouveau client</h2>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: C.slate }}>
            Crée le tenant + la config par défaut (règles KPI + mappings de statuts Batigest).
          </p>
        </div>

        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={S.label}>Nom de l'entreprise *</label>
            <input
              autoFocus type="text" style={S.input} value={nom}
              onChange={(e) => setNom(e.target.value)}
              placeholder="Ex: Constructions Dupont"
            />
          </div>

          <div>
            <label style={S.label}>
              Sous-domaine *{" "}
              <span style={{ fontWeight: 400, color: C.slateLight }}>
                (devient {schema || "..."}.bativiz.fr)
              </span>
            </label>
            <input
              type="text"
              style={{ ...S.input, fontFamily: mono }}
              value={schema}
              onChange={(e) => { setSchema(e.target.value.toLowerCase()); setSchemaTouched(true); }}
              placeholder="constructions-dupont"
            />
          </div>

          <div>
            <label style={S.label}>
              Email du dirigeant{" "}
              <span style={{ fontWeight: 400, color: C.slateLight }}>(optionnel)</span>
            </label>
            <input
              type="email" style={S.input} value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@constructions-dupont.fr"
            />
            <div style={{ fontSize: 11, color: C.slate, marginTop: 4 }}>
              Si renseigné, l'invitation sera à envoyer manuellement via Edge Function invite-user après création.
            </div>
          </div>

          {formError && (
            <div style={{
              padding: "9px 12px", borderRadius: 6, fontSize: 12,
              background: "#FEE2E2", color: "#991B1B", border: "1px solid #FECACA",
            }}>
              {formError}
            </div>
          )}
        </div>

        <div style={{
          padding: "14px 24px", borderTop: `1px solid ${C.border}`, background: "#FAFBFC",
          display: "flex", justifyContent: "flex-end", gap: 10,
        }}>
          <button onClick={onClose} disabled={saving} style={S.btnSecondary}>Annuler</button>
          <button
            onClick={handleSubmit} disabled={saving}
            style={{ ...S.btnPrimary, background: saving ? C.slateLight : C.accent, cursor: saving ? "wait" : "pointer" }}
          >
            {saving ? "Création…" : "Créer le tenant"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   MODALE — Suppression tenant (2 étapes : preview + confirm)
   ═══════════════════════════════════════════════════════════════════════════════ */
function DeleteTenantModal({ state, onCancel, onConfirm }) {
  const { tenant, summary, loading } = state;

  return (
    <div style={S.modalOverlay} onClick={onCancel}>
      <div style={S.modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={{
          padding: "20px 24px", borderBottom: `1px solid ${C.border}`,
          background: "#FEF2F2",
        }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.red }}>
            Supprimer le tenant
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: C.slate }}>
            Action irréversible — toutes les données bv_* seront cascadées.
          </p>
        </div>

        <div style={{ padding: "20px 24px" }}>
          {loading && !summary ? (
            <div style={{ fontSize: 13, color: C.slate, padding: "16px 0", textAlign: "center" }}>
              Calcul du résumé…
            </div>
          ) : summary ? (
            <>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: C.slate, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>
                  Cible
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.navy }}>
                  {summary.nom}
                </div>
                <div style={{ fontSize: 12, color: C.slate, fontFamily: mono, marginTop: 2 }}>
                  {summary.schema_name} · {tenant.id.substring(0, 8)}…
                </div>
              </div>

              <div style={{
                display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 10, marginBottom: 16,
              }}>
                <DeleteStat label="Utilisateurs" value={summary.nb_users} />
                <DeleteStat label="Chantiers" value={summary.nb_chantiers} />
                <DeleteStat label="Factures" value={summary.nb_factures} />
              </div>

              <div style={{
                padding: "12px 14px", borderRadius: 8,
                background: "#FEF3C7", border: "1px solid #FCD34D",
                fontSize: 12, color: "#92400E", lineHeight: 1.5,
              }}>
                ⚠ <strong>Cette action est irréversible.</strong> Toutes les données du tenant
                (chantiers, factures, pointages, etc.) seront supprimées via ON DELETE CASCADE.
                Les comptes utilisateurs liés à ce tenant resteront dans auth.users mais perdront
                leur lien tenant_id.
              </div>
            </>
          ) : null}
        </div>

        <div style={{
          padding: "14px 24px", borderTop: `1px solid ${C.border}`, background: "#FAFBFC",
          display: "flex", justifyContent: "flex-end", gap: 10,
        }}>
          <button onClick={onCancel} disabled={loading} style={S.btnSecondary}>
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={loading || !summary}
            style={{ ...S.btnDanger, opacity: (loading || !summary) ? 0.5 : 1, cursor: (loading || !summary) ? "not-allowed" : "pointer" }}
          >
            {loading ? "Suppression…" : "Confirmer la suppression"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteStat({ label, value }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 8, padding: "10px 12px",
      border: `1px solid ${C.border}`, textAlign: "center",
    }}>
      <div style={{
        fontSize: 10, color: C.slate, fontWeight: 600,
        textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4,
      }}>
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: C.red, fontFamily: mono }}>
        {value ?? 0}
      </div>
    </div>
  );
}
